import type { ISerialize } from '@/objects/ISerialize';
import { FileArchiveAccess } from './FileArchiveAccess';
import type { FileArchiveTypes } from './../enums/FileArchiveTypes';
import { formatDateIso } from '@/utilities/dateutils';

export class FileArchiveItem implements ISerialize {
    public readonly Access: FileArchiveAccess | null;

    public readonly ArchiveType: FileArchiveTypes;

    public Caption: string | null;

    public readonly Created: Date;

    public readonly CreatedByName: string | null;

    public readonly CreatedByUserId: number;

    public readonly Deleted: Date | null;

    public readonly DeletedById: number | null;

    public Description: string | null;

    public readonly Extension: string | null;

    public readonly FileGuid: string;

    public readonly FileName: string | null;

    public readonly FileSize: number;

    public readonly LastModified: Date;

    public readonly MimeType: string | null;

    public readonly Published: Date | null;

    public readonly PublishedById: number | null;

    public readonly ReadOnly: boolean;

    public readonly Tags: string[];

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Access?: FileArchiveAccess | null;
        ArchiveType?: FileArchiveTypes;
        Caption?: string | null;
        Created?: Date;
        CreatedByName?: string | null;
        CreatedByUserId?: number;
        Deleted?: Date | null;
        DeletedById?: number | null;
        Description?: string | null;
        Extension?: string | null;
        FileGuid?: string;
        FileName?: string | null;
        FileSize?: number;
        LastModified?: Date;
        MimeType?: string | null;
        Published?: Date | null;
        PublishedById?: number | null;
        ReadOnly?: boolean;
        Tags?: string[];
    }) {
        this.Access = values.Access ?? null;

        this.ArchiveType = values.ArchiveType ?? 'UNKNOWN';

        this.Caption = values.Caption ?? null;

        this.Created = values.Created ?? new Date('1970-01-01T00:00:00.000');

        this.CreatedByName = values.CreatedByName ?? '';

        this.CreatedByUserId = values.CreatedByUserId ?? 0;

        this.Deleted = values.Deleted ?? null;

        this.DeletedById = values.DeletedById ?? null;

        this.Description = values.Description ?? null;

        this.Extension = values.Extension ?? '';

        this.FileGuid = values.FileGuid ?? '';

        this.FileName = values.FileName ?? '';

        this.FileSize = values.FileSize ?? 0;

        this.LastModified = values.LastModified ?? new Date('1970-01-01T00:00:00.000');

        this.MimeType = values.MimeType ?? '';

        this.Published = values.Published ?? null;

        this.PublishedById = values.PublishedById ?? null;

        this.ReadOnly = values.ReadOnly ?? false;

        this.Tags = values.Tags ?? [];
    }

    public serialize(): string {
        return (
            '{' +
            `"Access": ${!this.Access ? null : this.Access?.serialize()}` +
            ',' +
            `"ArchiveType": ${this.ArchiveType == 'UNKNOWN' ? 0 : this.ArchiveType == 'REPORT' ? 1 : this.ArchiveType == 'CONTESTANT_IMPORT' ? 2 : this.ArchiveType == 'DIPLOMA' ? 3 : this.ArchiveType == 'UPLOADED' ? 4 : this.ArchiveType == 'EVENTCREATE_EXTERNALDATASOURCE' ? 5 : 0}` +
            ',' +
            `"Caption": ${JSON.stringify(this.Caption)}` +
            ',' +
            `"Created": ${'"' + formatDateIso(this.Created) + '"'}` +
            ',' +
            `"CreatedByName": ${JSON.stringify(this.CreatedByName)}` +
            ',' +
            `"CreatedByUserId": ${JSON.stringify(this.CreatedByUserId)}` +
            ',' +
            `"Deleted": ${this.Deleted ? '"' + formatDateIso(this.Deleted) + '"' : null}` +
            ',' +
            `"DeletedById": ${JSON.stringify(this.DeletedById)}` +
            ',' +
            `"Description": ${JSON.stringify(this.Description)}` +
            ',' +
            `"Extension": ${JSON.stringify(this.Extension)}` +
            ',' +
            `"FileGuid": ${JSON.stringify(this.FileGuid)}` +
            ',' +
            `"FileName": ${JSON.stringify(this.FileName)}` +
            ',' +
            `"FileSize": ${JSON.stringify(this.FileSize)}` +
            ',' +
            `"LastModified": ${'"' + formatDateIso(this.LastModified) + '"'}` +
            ',' +
            `"MimeType": ${JSON.stringify(this.MimeType)}` +
            ',' +
            `"Published": ${this.Published ? '"' + formatDateIso(this.Published) + '"' : null}` +
            ',' +
            `"PublishedById": ${JSON.stringify(this.PublishedById)}` +
            ',' +
            `"ReadOnly": ${JSON.stringify(this.ReadOnly)}` +
            ',' +
            `"Tags": [ ${this.Tags.map((x) => JSON.stringify(x))} ]` +
            '}'
        );
    }

    /**
     * Creates an instance of FileArchiveItem from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: { Caption?: string | null; Description?: string | null }): FileArchiveItem {
        return new FileArchiveItem(values);
    }

    public static _FromServerJson(json: any): FileArchiveItem {
        return new FileArchiveItem({
            Access: json.Access ? FileArchiveAccess._FromServerJson(json.Access) : null,
            ArchiveType:
                json.ArchiveType == 0
                    ? 'UNKNOWN'
                    : json.ArchiveType == 1
                      ? 'REPORT'
                      : json.ArchiveType == 2
                        ? 'CONTESTANT_IMPORT'
                        : json.ArchiveType == 3
                          ? 'DIPLOMA'
                          : json.ArchiveType == 4
                            ? 'UPLOADED'
                            : json.ArchiveType == 5
                              ? 'EVENTCREATE_EXTERNALDATASOURCE'
                              : 'UNKNOWN',
            Caption: json.Caption ?? '',
            Created: json.Created instanceof Date ? json.Created : new Date(json.Created),
            CreatedByName: json.CreatedByName ?? '',
            CreatedByUserId: json.CreatedByUserId,
            Deleted: json.Deleted ? (json.Deleted instanceof Date ? json.Deleted : new Date(json.Deleted)) : null,
            DeletedById: json.DeletedById ?? null,
            Description: json.Description ?? '',
            Extension: json.Extension ?? '',
            FileGuid: json.FileGuid,
            FileName: json.FileName ?? '',
            FileSize: json.FileSize,
            LastModified: json.LastModified instanceof Date ? json.LastModified : new Date(json.LastModified),
            MimeType: json.MimeType ?? '',
            Published: json.Published ? (json.Published instanceof Date ? json.Published : new Date(json.Published)) : null,
            PublishedById: json.PublishedById ?? null,
            ReadOnly: json.ReadOnly,
            Tags: json.Tags,
        });
    }

    /**
     * Creates an instance of FileArchiveItem from another instance of FileArchiveItem.
     * @param other The FileArchiveItem you want to clone
     * @returns A cloned FileArchiveItem
     */
    public static FromOther(other: FileArchiveItem): FileArchiveItem {
        return FileArchiveItem._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return FileArchiveItem.FromOther(this);
    }

    static NAME_OF_CLASS = 'FileArchiveItem';
}
